import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { API_BASE_URL } from "@/config";
import type { ShowAnalytics } from "@/lib/browse/showAnalytics";
import { loadShowAnalytics } from "@/lib/browse/showAnalyticsClient";
import { siteStorage } from "@/lib/platform/siteStorage";
import {
  fieldsMatch,
  isNumberOrNull,
  isRecord,
  isStringOrNull,
  optionalFields,
  validatedJson,
} from "@/lib/ui/validatedJson";

/**
 * Pull Sheet picking and lifecycle (#342, ADR-0014), the customer-facing counterpart of
 * `usePublicBrowse`. Like that hook it never sends an auth header: it uses a plain `fetch()`, never
 * the shared `api` client (`@/api/client`), because this surface must work identically for an
 * anonymous visitor. The backend routes are unauthenticated and `include_in_schema=False` (see
 * `routes_public.py`), so the wire types are hand-written here too rather than generated.
 */
export type PullSheetItem = {
  stock_item_public_id: string;
  product_name: string;
  // How many copies of this unit the customer wants (#646); always >= 1 for a line on the sheet.
  // The review-cart subtotal counts `price * quantity`.
  quantity: number;
  price: string | null;
  condition: string | null;
  is_graded: boolean;
  grading_company: string | null;
  grade: string | null;
  image_url: string | null;
  // Catalog set and collector number (#530), captured at pick time, for telling two same-name
  // picks apart on the review list; null for a generic unit or a catalog miss.
  set_name: string | null;
  number: string | null;
  // Short printing or finish tag (#540), captured with set and number so two picks that share both
  // but differ by variant read apart; null for a generic or default unit.
  variant_label: string | null;
};

export type PullSheetStatus =
  | "open"
  | "presented"
  | "settled"
  | "expired"
  | "abandoned"
  // The customer's own way out of the line (TRO-674, amends ADR-0014). Terminal, and the only
  // status this device sees on a sheet it has just stopped tracking.
  | "withdrawn";

export type PullSheet = {
  public_id: string;
  status: PullSheetStatus;
  items: PullSheetItem[];
  subtotal: string;
  // The optional self-entered customer label (#467); null unless the customer typed one at
  // check-out. Echoed back here and shown in the vendor's queue.
  customer_label: string | null;
  // The customer's live place in line and the queue length while `presented` (#464); both null in
  // every other state. The waiting screen polls this and shows "You're #3 of 5 in line".
  queue_position: number | null;
  queue_total: number | null;
};

function isPullSheetItem(value: unknown): value is PullSheetItem {
  return isRecord(value) && typeof value.stock_item_public_id === "string"
    && typeof value.product_name === "string" && typeof value.quantity === "number"
    && typeof value.is_graded === "boolean"
    && fieldsMatch(value, ["price", "condition"], isStringOrNull)
    // Absent, not only null: every field below has a default on the backend's `PullSheetItemOut`,
    // and this SPA deploys separately from the API (ADR-0017), like the browse list it sits on.
    // `variant_label` (#540) is the newest. `isPublicBrowseItem` already reads a missing catalog
    // fact as unknown; rejecting here would leave the customer on a working storefront they can no
    // longer pick from.
    && optionalFields(
      value,
      ["grading_company", "grade", "image_url", "set_name", "number", "variant_label"],
      isStringOrNull,
    );
}

function isPullSheet(value: unknown): value is PullSheet {
  return isRecord(value) && typeof value.public_id === "string"
    && (value.status === "open" || value.status === "presented" || value.status === "settled"
      || value.status === "expired" || value.status === "abandoned"
      || value.status === "withdrawn")
    && Array.isArray(value.items) && value.items.every(isPullSheetItem)
    && typeof value.subtotal === "string"
    // Defaulted on `PullSheetOut` like the item-level facts above, so absent is contract-legal: an
    // anonymous sheet has no label and an unpresented one has no place in line, and the review
    // modal already reads both as not set.
    && optionalFields(value, ["customer_label"], isStringOrNull)
    && optionalFields(value, ["queue_position", "queue_total"], isNumberOrNull);
}

const pullSheetJson = (response: Response) =>
  validatedJson(response, isPullSheet, "Pull Sheet response was invalid");

const STORAGE_PREFIX = "trovie.pull-sheet.";
const INDEX_KEY = "trovie.pull-sheet-index";

/**
 * The message every locked pick control shows (TRO-673). Once a list is `presented` it is in the
 * Vendor's queue, ADR-0014 treats that hand-off as committed, and the server refuses changes, so
 * the customer must be told before the tap rather than by a failure afterwards. Exported so the
 * browse card, the item-detail page and this hook's own refusal use identical wording, and so a
 * test asserts against the message rather than a copy of it.
 *
 * Short enough to read as a clause, because it is used in three places: appended to a locked
 * control's spoken name, shown in the sticky bar's caption, and raised as an action error.
 * Storefront language (ADR-0014, TRO-132): "your list" and "in line", never "cart" or "order".
 */
export const PICKS_IN_LINE_MESSAGE =
  "your list is with the Vendor and can't be changed until they call you";

type PullSheetContext = {
  showPublicId?: string;
  participationId?: string;
};

type StoredPullSheetContext = {
  show_public_id: string;
  participation_id: string;
};

type PullSheetIndex = Record<string, StoredPullSheetContext>;

function pullSheetIndex(value: string | null): PullSheetIndex {
  if (!value) return {};
  try {
    const parsed: unknown = JSON.parse(value);
    if (!isRecord(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, StoredPullSheetContext] =>
        isRecord(entry[1]) && typeof entry[1].show_public_id === "string"
        && typeof entry[1].participation_id === "string"
      ),
    );
  } catch {
    return {};
  }
}

// On web the index is stored in a cookie, which is capped at 4KB, so it keeps only the most
// recently picked events. A list old enough to fall off is long since terminal.
const MAX_TRACKED_EVENTS = 12;

async function storePullSheetContext(eventId: string, context: PullSheetContext): Promise<void> {
  if (!context.showPublicId || !context.participationId) return;
  const index = pullSheetIndex(await siteStorage.getItem(INDEX_KEY));
  // Deleted before it is re-added so this event sorts as the newest, not where it first landed.
  delete index[eventId];
  index[eventId] = {
    show_public_id: context.showPublicId,
    participation_id: context.participationId,
  };
  const kept = Object.fromEntries(Object.entries(index).slice(-MAX_TRACKED_EVENTS));
  await siteStorage.setItem(INDEX_KEY, JSON.stringify(kept));
}

/**
 * The device-local `{event_public_id -> pull_sheet_id}` mapping (ADR-0014's guest-cart-style
 * resumability), stored under one `siteStorage` key per event so each event's list stays isolated.
 * A customer who reopens the same table's QR (or the same browser tab) on the same device resumes
 * their existing Pull Sheet instead of starting fresh. Switching devices or clearing storage loses
 * continuity, which ADR-0014 accepts, because no server-side identity exists to recover from.
 */
function usePullSheetId(
  eventId: string | null,
  context: PullSheetContext,
): readonly [string | null, (id: string | null) => Promise<void>] {
  const qc = useQueryClient();
  const { participationId, showPublicId } = context;

  // The pull_sheet_id lives in the shared React Query cache, keyed by event, not in per-instance
  // state, so every `usePullSheet` mounted for one event reads and writes one id. The browse list
  // and the item-detail page pushed on top of it stay in step, and a sheet created on either
  // screen is immediately visible to the other (#461). Keying by event also keeps another event's
  // id out. `enabled: false` with a no-op `queryFn` makes this an observer only: it never fetches
  // and only re-renders on cache writes. The seed read below loads device storage, guarded so a
  // pick that lands before storage resolves is not overwritten by the stored value.
  const idKey = ["pull-sheet-id", eventId] as const;
  const { data } = useQuery<string | null>({
    queryKey: idKey,
    queryFn: () => null,
    enabled: false,
    initialData: null,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  // Seed the shared cache from device storage once per event (ADR-0014 guest-cart resumability).
  const seededForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!eventId || seededForRef.current === eventId) return undefined;
    seededForRef.current = eventId;
    // Rebuild the key from `eventId` (already a dependency) rather than closing over the
    // render-scoped `idKey`, so this effect runs once per event instead of on every render.
    const key = ["pull-sheet-id", eventId] as const;
    let cancelled = false;
    void siteStorage.getItem(STORAGE_PREFIX + eventId).then((stored) => {
      // Do not overwrite an id a pick created while this read was in flight.
      if (cancelled || stored == null || qc.getQueryData(key) != null) return;
      qc.setQueryData(key, stored);
    });
    return () => {
      cancelled = true;
    };
  }, [eventId, qc]);

  const setId = (next: string | null) => {
    if (!eventId) return Promise.resolve();
    qc.setQueryData(idKey, next);
    if (next) {
      return Promise.all([
        siteStorage.setItem(STORAGE_PREFIX + eventId, next),
        storePullSheetContext(eventId, context),
      ]).then(() => undefined);
    }
    return siteStorage.removeItem(STORAGE_PREFIX + eventId);
  };

  useEffect(() => {
    if (!eventId || !data || !showPublicId || !participationId) return;
    void storePullSheetContext(eventId, { participationId, showPublicId });
  }, [data, eventId, participationId, showPublicId]);

  return [eventId ? data ?? null : null, setId] as const;
}

async function fetchPullSheet(pullSheetId: string): Promise<PullSheet> {
  const resp = await fetch(`${API_BASE_URL}/pull-sheet/${pullSheetId}`);
  if (resp.status === 404) throw new Error("This list is no longer available.");
  if (!resp.ok) throw new Error("Couldn't load your list.");
  return await pullSheetJson(resp);
}

/**
 * Why a withdraw returned 409, in the customer's terms (TRO-674). The server's `Conflict` only
 * says "not presented", and the possible reasons need different messages: a sheet the Vendor
 * settled means "go and see them", and a sheet that timed out means the opposite. So the real
 * state is re-read rather than assumed. The sheet query polls only every five seconds and stops
 * while the app is backgrounded, so a 409 proves the status this device last saw is stale.
 *
 * If that read also fails, falls back to the one statement true in every 409 case, so a customer
 * who tapped is never left without a response.
 */
async function withdrawConflictMessage(pullSheetId: string): Promise<string> {
  try {
    const sheet = await fetchPullSheet(pullSheetId);
    if (sheet.status === "settled") {
      return "The Vendor already pulled your list — check with them before you go.";
    }
    if (sheet.status === "expired" || sheet.status === "abandoned") {
      return "Your list already dropped out of the line — the Vendor didn't get to it.";
    }
    if (sheet.status === "withdrawn") return "You're already out of the line.";
  } catch {
    // An unreadable sheet is still a sheet that is not in line, so say that much.
  }
  return "Your list is no longer in line.";
}

// No Event `public_id`: the Show page identifies a list by its Vendor and leaves the return trip to
// the storefront redirect, so the id stays a storage key and never reaches a screen (ADR-0092).
export type LocalShowPullSheet = {
  participationId: string;
  pullSheetId: string;
  itemCount: number;
};

/** Active device-local lists for one public Show; terminal and unreadable sheets fall away. */
export function useLocalShowPullSheets(showPublicId: string | undefined) {
  return useQuery({
    queryKey: ["local-show-pull-sheets", showPublicId],
    enabled: !!showPublicId,
    gcTime: 0,
    queryFn: async (): Promise<LocalShowPullSheet[]> => {
      const index = pullSheetIndex(await siteStorage.getItem(INDEX_KEY));
      const records = Object.entries(index).filter(([, context]) =>
        context.show_public_id === showPublicId
      );
      const sheetIds = await Promise.all(
        records.map(([eventId]) => siteStorage.getItem(STORAGE_PREFIX + eventId)),
      );
      const sheets = await Promise.all(records.map(async ([, context], position) => {
        const pullSheetId = sheetIds[position];
        if (!pullSheetId) return null;
        try {
          const sheet = await fetchPullSheet(pullSheetId);
          return sheet.status === "open" || sheet.status === "presented"
            ? {
              participationId: context.participation_id,
              pullSheetId,
              itemCount: sheet.items.reduce((count, item) => count + item.quantity, 0),
            }
            : null;
        } catch {
          return null;
        }
      }));
      return sheets.filter((sheet): sheet is LocalShowPullSheet => sheet !== null);
    },
  });
}

/**
 * The full Pull Sheet picking surface for one Event's browse page: the resumed or created sheet
 * (`null` before any pick), the count and subtotal it carries, and the pick, unpick and present
 * actions. One hook rather than three, because every action needs the current `pullSheetId` from
 * local storage and writes it back on create; splitting them would move that coupling into the
 * caller.
 */
export function usePullSheet(eventId: string | null, context: PullSheetContext = {}) {
  const [pullSheetId, setPullSheetId] = usePullSheetId(eventId, context);
  const qc = useQueryClient();
  const queryKey = ["pull-sheet", pullSheetId] as const;
  // Memoized because the settle effect below depends on it.
  const { showPublicId } = context;
  const invalidateLocalShowPullSheets = useCallback(() => {
    if (!showPublicId) return;
    void qc.invalidateQueries({ queryKey: ["local-show-pull-sheets", showPublicId] });
  }, [qc, showPublicId]);

  const sheetQuery = useQuery({
    queryKey,
    enabled: !!pullSheetId,
    queryFn: () => {
      if (!pullSheetId) throw new Error("no pull sheet");
      return fetchPullSheet(pullSheetId);
    },
    // While the sheet is `presented`, poll so the customer's queue position (#464) counts down as
    // the vendor works the line, and so a server-side change to expired or abandoned is noticed
    // promptly. Every other state is terminal or still being edited, so there is nothing to poll.
    refetchInterval: (query) => query.state.data?.status === "presented" ? 5000 : false,
  });

  // A synchronously updated mirror of `pullSheetId`. The pick requests read this rather than the
  // state value, so a pick fired before React re-renders still sees the id the previous pick just
  // created; a state closure would still hold the null it captured at the tap.
  const pullSheetIdRef = useRef(pullSheetId);
  useEffect(() => {
    pullSheetIdRef.current = pullSheetId;
  }, [pullSheetId]);

  // Whether the list this device holds is already with the Vendor (TRO-673). Everything the
  // customer may no longer do follows from this: the browse and detail steppers render unavailable,
  // the write actions below refuse, and the id write-back is frozen. The server resolves
  // `presented` to expired or abandoned on its own, and the query polls every 5s while it holds, so
  // this unlocks by itself once the sheet leaves the queue.
  const isPresented = sheetQuery.data?.status === "presented";
  // Mirrored into a ref for the same reason as `pullSheetIdRef`: a serialized write can resolve
  // between renders and must read the current status, not the status of the render that queued it.
  const presentedRef = useRef(isPresented);
  useEffect(() => {
    presentedRef.current = isPresented;
  }, [isPresented]);

  /**
   * Whether this device watched the Vendor settle its list. Kept in the shared cache keyed by
   * event, like the id, so the browse list and the item detail page pushed on top of it confirm one
   * settle between them rather than one each. Session-only, never AsyncStorage: the message is for
   * the customer standing at the table, not for whoever reopens the page tomorrow.
   */
  const settledKey = ["pull-sheet-settled", eventId] as const;
  const { data: justSettled } = useQuery<boolean>({
    queryKey: settledKey,
    queryFn: () => false,
    enabled: false,
    initialData: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  /**
   * `settled` is terminal, so it is the last status this device hears for the sheet. Forget the id,
   * which drops the sticky bar and lets the next pick start fresh, and keep the settled fact, since
   * clearing the id leaves no sheet to read a status from.
   *
   * Only a transition this device watched is confirmed. A page opened on an already-settled sheet
   * still drops the stale id, but nobody is waiting to be told.
   */
  const status = sheetQuery.data?.status;
  const watchedInLineRef = useRef(false);
  useEffect(() => {
    if (status === "presented") watchedInLineRef.current = true;
    if (status !== "settled") return;
    const watched = watchedInLineRef.current;
    watchedInLineRef.current = false;
    if (watched) qc.setQueryData(["pull-sheet-settled", eventId], true);
    pullSheetIdRef.current = null;
    void setPullSheetId(null).then(invalidateLocalShowPullSheets);
  }, [status, eventId, qc, setPullSheetId, invalidateLocalShowPullSheets]);

  /** Drop the settle confirmation once the customer has read it. */
  const acknowledgeSettled = () => {
    qc.setQueryData(settledKey, false);
  };

  /**
   * Adopt the id the server just returned as the device's sheet for this event, in device storage
   * and in the shared cache.
   *
   * Skipped while the held sheet is `presented` (TRO-673). A response in that state is either the
   * same sheet, where storing it again changes nothing, or a fork of a list already in the vendor's
   * queue. Adopting a fork's id would make the customer's place in line unreachable, because the
   * queue screen is found only through the id this device stored. The server no longer creates
   * such a fork; this guard also covers an API that predates the rule (ADR-0017 ships this SPA
   * separately from the backend).
   *
   * A stale id (unknown, another event's, or timed out of the queue) is a different case: the sheet
   * it names is not `presented`, so its replacement is adopted as ADR-0014's guest-cart
   * resumability intends.
   */
  const adoptSheetId = (sheet: PullSheet) => {
    if (presentedRef.current) return;
    pullSheetIdRef.current = sheet.public_id;
    void setPullSheetId(sheet.public_id).then(invalidateLocalShowPullSheets);
  };

  /**
   * A refused write means this device's view of the sheet is out of date, so re-read it and return
   * the message the customer sees meanwhile (TRO-673).
   *
   * A `409` from either pick route has two server-side causes (the show stopped operating, or the
   * list is already with the Vendor), and the body names the cause only in prose that is not parsed
   * here, so the message stays neutral rather than risk a false one. The refetch is what matters:
   * either way the sheet's status changed, and re-reading locks or unlocks the steppers. Without it
   * the client could hold a stale `open` indefinitely, because the 5s poll runs only while the held
   * status is `presented`.
   */
  const refetchOnConflict = (resp: Response, message: string) => {
    if (resp.status === 409 && pullSheetIdRef.current) {
      void qc.invalidateQueries({ queryKey: ["pull-sheet", pullSheetIdRef.current] });
    }
    return message;
  };

  // Serialize every pick write (addPick, removePick, setQuantity) into one chain so they run one at
  // a time (#462). Otherwise two different items picked before the first request settles would both
  // POST with `pull_sheet_id: null`, the server would create two pull sheets, and the second would
  // orphan the first item. Serialized, the first pick creates the sheet and stamps
  // `pullSheetIdRef`, and every later pick appends to that sheet. It also prevents the
  // last-write-wins cache overwrite that two concurrent appends would cause.
  const writeChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const serialize = <T>(task: () => Promise<T>): Promise<T> => {
    const run = writeChainRef.current.then(task, task);
    // Keep the chain alive even if a task rejects, so one failed pick cannot block the rest.
    writeChainRef.current = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  };

  /**
   * Fire-and-forget anonymous funnel events (TRO-642). The Pull Sheet lifecycle is tracked here
   * rather than in the two screens that mount this hook, because both screens are mounted at once
   * (the item detail route is pushed on top of the browse list) and observe the same shared React
   * Query cache, so a screen watching sheet state would fire twice for one pick. A mutation
   * callback runs only on the instance whose `mutate()` was called, so this is the one place the
   * count is right.
   *
   * Never awaited and never allowed to reject: a customer's pick must not depend on an analytics
   * load, and on native `loadShowAnalytics` is a no-op.
   */
  const track = (fire: (analytics: ShowAnalytics) => void) => {
    // Reuses only the id of the Show the caller already knows (ADR-0106). `showEndsAt` stays null
    // because a browse page outlives its Show and must not clear the identity.
    void loadShowAnalytics({ showId: showPublicId ?? null, showEndsAt: null })
      .then(fire)
      .catch(() => {});
  };

  // Items with a pick mutation in flight, keyed by `stock_item_public_id`, so `togglePick()` can
  // refuse a second tap on the same item until the first settles. `isPicked()` reflects server
  // state only after `onSuccess`, so two rapid taps would otherwise read the same stale value and
  // race.
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const markPending = (stockItemPublicId: string) =>
    setPendingIds((prev) => new Set(prev).add(stockItemPublicId));

  // One customer-facing error channel for the write actions (#360). Each mutation's `onError` sends
  // its customer-worded message (`Couldn't add that card.`, and so on) here. The browse screen
  // shows it as a transient toast for a list quick-pick, and the review modal shows it inline for a
  // remove or check-out. Cleared when the next action starts (`onMutate`) or the customer dismisses
  // it, so a failed write never leaves the UI unchanged without explanation.
  const [actionError, setActionError] = useState<string | null>(null);
  const clearActionError = useCallback(() => setActionError(null), []);
  const clearPending = (stockItemPublicId: string) =>
    setPendingIds((prev) => {
      if (!prev.has(stockItemPublicId)) return prev;
      const next = new Set(prev);
      next.delete(stockItemPublicId);
      return next;
    });

  const addPick = useMutation({
    mutationFn: (stockItemPublicId: string) =>
      serialize(async () => {
        if (!eventId) throw new Error("no event");
        // Read before the write: this is the request that turns browsing into having a list, and
        // after the response every pick looks identical. A resumed sheet (ADR-0014) already has an
        // id here, so a returning customer does not re-enter the funnel.
        const startsSheet = pullSheetIdRef.current === null;
        const resp = await fetch(`${API_BASE_URL}/events/${eventId}/pull-sheet/picks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            stock_item_public_id: stockItemPublicId,
            pull_sheet_id: pullSheetIdRef.current,
          }),
        });
        if (!resp.ok) throw new Error(refetchOnConflict(resp, "Couldn't add that card."));
        const sheet = await pullSheetJson(resp);
        // Stamp the id synchronously so the next serialized pick appends rather than creating
        // another sheet (`onSuccess` runs too late for the write queued behind this one). Frozen
        // while the held sheet is presented, for the reason `adoptSheetId` gives.
        if (!presentedRef.current) pullSheetIdRef.current = sheet.public_id;
        if (startsSheet) track((analytics) => analytics.pullSheetStarted(eventId));
        return sheet;
      }),
    onMutate: (stockItemPublicId) => {
      setActionError(null);
      markPending(stockItemPublicId);
    },
    onError: (e) => setActionError(e.message),
    onSettled: (_data, _error, stockItemPublicId) => clearPending(stockItemPublicId),
    onSuccess: (sheet) => {
      qc.setQueryData(["pull-sheet", sheet.public_id], sheet);
      adoptSheetId(sheet);
    },
  });

  const removePick = useMutation({
    mutationFn: (stockItemPublicId: string) =>
      serialize(async () => {
        const sheetId = pullSheetIdRef.current;
        if (!sheetId) throw new Error("no pull sheet");
        const resp = await fetch(`${API_BASE_URL}/pull-sheet/${sheetId}/picks/remove`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ stock_item_public_id: stockItemPublicId }),
        });
        if (!resp.ok) throw new Error("Couldn't remove that card.");
        return await pullSheetJson(resp);
      }),
    onMutate: (stockItemPublicId) => {
      setActionError(null);
      markPending(stockItemPublicId);
    },
    onError: (e) => setActionError(e.message),
    onSettled: (_data, _error, stockItemPublicId) => clearPending(stockItemPublicId),
    onSuccess: (sheet) => {
      qc.setQueryData(["pull-sheet", sheet.public_id], sheet);
      invalidateLocalShowPullSheets();
    },
  });

  // Set the absolute picked quantity for one unit (#646), the write behind the browse card and
  // review-cart steppers. Serialized through the same chain as `addPick`, so a first quantity set
  // on a fresh device creates exactly one sheet and later sets append to it, and it stamps the id
  // the same way. `quantity` 0 removes the line; the server clamps anything above the unit's live
  // availability.
  const setQuantity = useMutation({
    mutationFn: (vars: { stockItemPublicId: string; quantity: number; }) =>
      serialize(async () => {
        if (!eventId) throw new Error("no event");
        const startsSheet = pullSheetIdRef.current === null;
        const resp = await fetch(`${API_BASE_URL}/events/${eventId}/pull-sheet/picks/quantity`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            stock_item_public_id: vars.stockItemPublicId,
            quantity: vars.quantity,
            pull_sheet_id: pullSheetIdRef.current,
          }),
        });
        if (!resp.ok) throw new Error(refetchOnConflict(resp, "Couldn't update the quantity."));
        const sheet = await pullSheetJson(resp);
        if (!presentedRef.current) pullSheetIdRef.current = sheet.public_id;
        // The stepper is the other way a first pick happens, so it also starts the funnel, but only
        // when the customer took something. A 0 with no sheet is a stepper tapped down from
        // nothing, and counting it would report a started list that never had an item.
        if (startsSheet && vars.quantity > 0) {
          track((analytics) => analytics.pullSheetStarted(eventId));
        }
        return sheet;
      }),
    onMutate: (vars) => {
      setActionError(null);
      markPending(vars.stockItemPublicId);
    },
    onError: (e) => setActionError(e.message),
    onSettled: (_data, _error, vars) => clearPending(vars.stockItemPublicId),
    onSuccess: (sheet) => {
      qc.setQueryData(["pull-sheet", sheet.public_id], sheet);
      adoptSheetId(sheet);
    },
  });

  const present = useMutation({
    // The optional self-entered customer label (#467), undefined when the customer typed nothing;
    // the body then carries a null label and the sheet stays anonymous. The server normalizes the
    // label (trim, empty, truncate), so nothing is validated here.
    mutationFn: async (customerLabel?: string) => {
      if (!pullSheetId) throw new Error("no pull sheet");
      const resp = await fetch(`${API_BASE_URL}/pull-sheet/${pullSheetId}/present`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_label: customerLabel ?? null }),
      });
      if (!resp.ok) throw new Error("Couldn't send your list to the Vendor — try again.");
      return await pullSheetJson(resp);
    },
    onMutate: () => setActionError(null),
    onError: (e) => setActionError(e.message),
    onSuccess: (sheet) => {
      qc.setQueryData(queryKey, sheet);
      invalidateLocalShowPullSheets();
      // The outcome the browse surface exists to produce: the list is now in the vendor's queue.
      // Only the two counts are sent; `subtotal` and `customer_label` are on the same object and
      // deliberately stay out of analytics (ADR-0014, ADR-0076).
      if (eventId) {
        track((analytics) =>
          analytics.pullSheetPresented(eventId, {
            lineCount: sheet.items.length,
            itemCount: sheet.items.reduce((total, item) => total + item.quantity, 0),
          })
        );
      }
    },
  });

  /**
   * Take this list back out of the vendor's line: `presented -> withdrawn` (TRO-674, amends
   * ADR-0014). A bodyless POST, because a withdraw carries only intent and the path names the
   * sheet.
   *
   * Not run through `serialize()`, like `present`: that chain stops two concurrent picks from each
   * creating a sheet, and a lifecycle transition neither creates nor appends to one.
   *
   * On success the device-local id is dropped, so nothing stale is left for a later pick to resume
   * and the next `+` starts a fresh `open` sheet instead of appending to one that has left the
   * line. The ref is cleared synchronously with the stored value, so a pick fired before React
   * re-renders does not see the withdrawn id.
   */
  const withdraw = useMutation({
    mutationFn: async () => {
      const sheetId = pullSheetIdRef.current;
      if (!sheetId) throw new Error("no pull sheet");
      const resp = await fetch(`${API_BASE_URL}/pull-sheet/${sheetId}/withdraw`, {
        method: "POST",
      });
      // A 409 means the sheet is no longer `presented`. Reporting success would tell a customer
      // they are out of a line they may still be at the front of, so re-read the sheet and say how
      // it left: "the Vendor pulled it" and "it timed out" send the customer to opposite places.
      if (resp.status === 409) throw new Error(await withdrawConflictMessage(sheetId));
      if (!resp.ok) throw new Error("Couldn't take your list out of the line — try again.");
      return await pullSheetJson(resp);
    },
    onMutate: () => setActionError(null),
    onError: (e) => setActionError(e.message),
    onSuccess: (sheet) => {
      qc.setQueryData(["pull-sheet", sheet.public_id], sheet);
      pullSheetIdRef.current = null;
      void setPullSheetId(null).then(invalidateLocalShowPullSheets);
    },
  });

  const isPicked = (stockItemPublicId: string) =>
    !!sheetQuery.data?.items.some((i) => i.stock_item_public_id === stockItemPublicId);

  // How many copies of this unit are on the sheet (#646); 0 when it is not picked.
  const pickedQty = (stockItemPublicId: string) =>
    sheetQuery.data?.items.find((i) => i.stock_item_public_id === stockItemPublicId)?.quantity ?? 0;

  const isPickPending = (stockItemPublicId: string) => pendingIds.has(stockItemPublicId);

  /**
   * Refuse a pick write while the list is in the Vendor's queue, and say why (TRO-673). Returns
   * whether it refused, so each action can stop.
   *
   * Every pick control on both browse surfaces also renders unavailable while `isPresented`, which
   * is what a customer reads. This is the backstop: one place, shared by both actions, that a stale
   * render, a control that ignores the flag, or a later caller cannot bypass, and it explains
   * itself rather than silently dropping the tap.
   */
  const refuseWhileInLine = () => {
    if (!isPresented) return false;
    setActionError(`Nothing to change — ${PICKS_IN_LINE_MESSAGE}.`);
    return true;
  };

  // Set this unit's absolute picked quantity (#646), with the same in-flight guard as `togglePick`,
  // so a stepper tap while the previous one settles is a no-op rather than a racing write.
  const setPickQuantity = (stockItemPublicId: string, quantity: number) => {
    if (isPickPending(stockItemPublicId)) return;
    if (refuseWhileInLine()) return;
    setQuantity.mutate({ stockItemPublicId, quantity: Math.max(0, quantity) });
  };

  // The pick toggle both browse surfaces call (the list grid or rows, and the item-detail page), so
  // the pending guard lives here once rather than per screen. A second tap is refused while this
  // item's mutation is in flight, because `isPicked()` reflects server state only after
  // `onSuccess`.
  const togglePick = (stockItemPublicId: string) => {
    if (isPickPending(stockItemPublicId)) return;
    if (refuseWhileInLine()) return;
    if (isPicked(stockItemPublicId)) removePick.mutate(stockItemPublicId);
    else addPick.mutate(stockItemPublicId);
  };

  return {
    pullSheetId,
    sheet: sheetQuery.data ?? null,
    isLoading: sheetQuery.isLoading && !!pullSheetId,
    // The list is with the Vendor and can no longer be edited (TRO-673).
    isPresented,
    // The Vendor pulled this list while the device was watching, and its id has since been
    // forgotten. The surface owes the customer a confirmation, then `acknowledgeSettled()`.
    justSettled,
    acknowledgeSettled,
    // Why picking is unavailable, or `null` while the list is open, passed straight to a pick
    // control, which renders itself unavailable and shows this. Derived here rather than per screen
    // so the two browse surfaces cannot drift apart on the locking condition or wording.
    pickLockedReason: isPresented ? PICKS_IN_LINE_MESSAGE : null,
    isPicked,
    pickedQty,
    isPickPending,
    togglePick,
    setPickQuantity,
    addPick,
    removePick,
    setQuantity,
    present,
    withdraw,
    // The shared customer-facing error for the last failed write action, and a way to dismiss it
    // (#360). Null when the last action succeeded or nothing has failed yet.
    actionError,
    clearActionError,
  };
}
